import React, { useState, useEffect } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Input,
  Badge,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Textarea,
  Flex
} from "@chakra-ui/react";
import { toast } from "react-toastify";
import { useAppContext } from "../../contexts/AppContext";
import useTasks from "../../hooks/useTasks";
import CustomReactSelect from "../general/form/CustomReactSelect";
import FileUpload from "../general/form/FileUpload";
import Fieldset from "../general/form/Fieldset";
import CloseModalArrow from "../general/CloseModalArrow";

const TaskDrawer = ({ isOpen, onClose, onSave, task }) => {
  const { data } = useAppContext();
  const { isOverdue } = useTasks();

  const [formData, setFormData] = useState({
    label: "",
    fund: "",
    dueDate: "",
    completed: false,
  });
  const [isDirty, setIsDirty] = useState(false); // Track if changes are made

  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const cancelRef = React.useRef();
  const today = new Date().toISOString().split("T")[0];
  const categoryOptions = [
    { value: "valuation", label: "Valuation" },
    { value: "risk", label: "Risk" },
    { value: "portfolio management", label: "Portfolio management" },
    { value: "oversight", label: "Oversight" },
    { value: "operation", label: "Operation" },
    { value: "compliance", label: "Compliance" },
  ];
  const handleCategoryChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      category: selectedOption ? selectedOption.value : "",
    }));
    setIsDirty(true);
  };



  useEffect(() => {
    if (task) {
      setFormData(task);
    } else {
      setFormData({ label: "", fund: "", dueDate: "", completed: false });
    }
    setIsDirty(false);
  }, [task]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setIsDirty(true); // Mark as dirty on any change
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      fund: selectedOption ? selectedOption.value : "",
    }));
    setIsDirty(true);
  };

  const handleFileChange = (files) => {
    setFormData((prev) => ({
      ...prev,
      attachments: files,
    }));
    setIsDirty(true);
  };

  const handleSubmit = () => {
    onSave(formData);
    onClose();
    toast.success("Task saved successfully!");
  };

  const handleDrawerClose = () => {
    if (isDirty) {
      onConfirmOpen();
    } else {
      onClose();
    }
  };

  const markAsCompleted = () => {
    const updatedTask = { ...formData, completed: true };
    onSave(updatedTask);
    onClose();
    toast.success("Task completed!");
  };




  const fundOptions = data
    .sort((a, b) => a.fundName.localeCompare(b.fundName))
    .map((fund) => ({
      value: fund.fundName,
      label: fund.fundName,
    }));

  

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={handleDrawerClose} size="lg">
        <DrawerOverlay />
        <DrawerContent w={"640px"}>
          <DrawerHeader justifyContent={"space-between"} alignItems={"center"} borderBottom={"1px solid"} borderColor={"grey.200"}>
            <Flex gap={4} alignItems={"center"}>
            <CloseModalArrow onClose={handleDrawerClose} />
            {task ? "Edit Task" : "Add New Task"}

            </Flex>
            
            {task ? (<Badge colorScheme={formData.completed ? "green" : isOverdue(formData.dueDate) ? "red" : "yellow"}>
              {formData.completed ? "Completed" : isOverdue(formData.dueDate) ? "Overdue" : "Pending"}
            </Badge>
            ) : ("")}          
            
            </DrawerHeader>
          <DrawerBody p={8}>
        
        
        <Fieldset
          label={"Task Name"} 
          description={"Enter a brief, descriptive name for this task."} 
          input={
            <Input
                  name="label"
                  value={formData.label}
                  onChange={handleChange}
                  placeholder="Task name"
            />
          }
        />

<Fieldset
          label={"Fund"}
          description={"Select the fund related to this task."}
          input={
            <CustomReactSelect
              options={fundOptions}
              value={fundOptions.find((option) => option.value === formData.fund)}
              onChange={handleSelectChange}
              placeholder="Select fund"
              isClearable
            />
          }
        />
          
         
         
      <Fieldset
          label={"Due Date"}
          description={"Specify the deadline for completing this task."}
          input={
            <Input
                style={{ width: "fit-content" }}
                type="date"
                name="dueDate"
                min={today}
                value={formData.dueDate}
                onChange={handleChange}
            />
          }
        />

          
          <Fieldset
          label={"Category"}
          description={"Specify the category of this task."}
          input={
            <CustomReactSelect
              options={categoryOptions}
              value={categoryOptions.find((option) => option.value === formData.category)}
              onChange={handleCategoryChange}
              placeholder="Select category"
              isClearable
            />
          }
        />

          <Fieldset
            label={"Comment"}
            description={"Provide additional details or notes about the task."}
            input={
              <Textarea
                name="description"
                placeholder="Enter a comment..."
                value={formData.description || ""}
                onChange={handleChange}
              />
            }
          />

         <Fieldset
          label={"Attachments"}
          description={"Upload any relevant documents."}
          input={
            <FileUpload
            allowMultiple={true}
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx, .xls, .xlsx" 
            />
          }
        />




          


   
        </DrawerBody>
          <DrawerFooter borderTop={"1px solid"} borderColor={"grey.200"} gap={2}>
            {task && !task.completed ? (<Button variant={"outline"} onClick={markAsCompleted}>Mark as completed</Button>) : (<></>)}
            <Button colorScheme="brand" onClick={handleSubmit} isDisabled={!isDirty || !formData.dueDate || !formData.label} flex={1}>
              {task ? "Save changes" : "Create task"}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Confirmation AlertDialog */}
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Discard Changes?
            </AlertDialogHeader>
            <AlertDialogBody>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant={"outline"}>
                Cancel
              </Button>
              <Button colorScheme="error" onClick={() => {
                onConfirmClose();
                onClose();
              }} ml={3}>
                Discard
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default TaskDrawer;
